import React, { Component, type ErrorInfo, type ReactNode } from 'react'

import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from 'react-query'
import { SWRConfig } from 'swr'

import { initTelemetry } from '~/utils/telemetry'

import App from './App'

initTelemetry()

if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_MSW) {
  const { worker } = require('./mocks/browser')
  worker.start()
}

const queryClient = new QueryClient()

class RootErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('RootErrorBoundary', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 24,
            fontFamily: 'system-ui,sans-serif',
            maxWidth: 720
          }}
        >
          <h1 style={{ fontSize: 18 }}>Clinic UI failed to render</h1>
          <p style={{ color: '#666', fontSize: 14 }}>
            Open DevTools → Console for details. If you changed region in
            browser storage, try clearing key <code>clinic.app_region</code>{' '}
            (must be <code>cn</code> or <code>global</code>).
          </p>
          <pre
            style={{
              marginTop: 16,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
        </div>
      )
    }
    return this.props.children
  }
}

function renderApp() {
  const rootElement = document.getElementById('root')
  if (!rootElement) {
    throw new Error('Root element not found')
  }
  const root = createRoot(rootElement)
  root.render(
    <React.StrictMode>
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <SWRConfig value={{ revalidateOnFocus: false }}>
            <App />
          </SWRConfig>
        </QueryClientProvider>
      </RootErrorBoundary>
    </React.StrictMode>
  )
}

async function main() {
  // hack tidbcloud sso callback url
  // the tidbcloud sso callback url conflicts with the hash router
  // example:
  // http://localhost:8181/clinic/?oauth_callback=1#access_token=xxx&scope=openid%20email&expires_in=7200&token_type=Bearer&state=xxx&id_token=xxx

  if (
    window.location.search === '?oauth_callback=1' &&
    window.location.hash.startsWith('#access_token=')
  ) {
    const newUrl = window.location.href.replace('#', '&')
    window.location.assign(newUrl)
    return
  }

  // OAuth2 code 回调 + HashRouter：IdP 只回 query，常无 hash。
  // 用 replaceState 静默补 #/login（仅改 hash 不会触发页面重载），然后继续 renderApp。
  const sp = new URLSearchParams(window.location.search)
  if (sp.has('oauth_callback') && sp.has('code') && sp.has('state')) {
    if (!window.location.hash) {
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}#/login`
      )
    }
  }

  renderApp()
}

main()
